/**
 * The member Timeline tab's copy and pure row helpers (DNKADM-0429), as a JSX-free module.
 *
 * dink-admin has no locale files; its user-facing strings live in code. This module is
 * the one place the Timeline's strings live (the `staffChangeCopy.ts` pattern), so the
 * component file exports only components and the tests assert against the same constants
 * the component renders.
 *
 * Backend text (`title`, actor name/email) is NOT copy: it is scrubbed at the render
 * boundary with `scrubBrand`, never stored here.
 */
import type { CancelCreditOutcome, CancelCreditReason, TimelineActor, TimelineCategory, TimelineItem } from '@/api/memberTimeline';
import type { ActivityKind } from '@/api/audit';
import { expiryLabel } from '@/utils/creditExpiry';
import { formatCreditAmount } from '@/utils/v2Format';

export const TIMELINE_COPY = {
  tabLabel: 'Timeline',
  listLabel: 'Member timeline',
  filtersLabel: 'Filter by category',
  loadMore: 'Load more',
  loadingMore: 'Loading…',
  loadingLabel: 'Loading timeline',
  emptyTitle: 'Nothing on the timeline yet',
  emptyFilteredTitle: 'Nothing matches these filters',
  emptyBody: 'Bookings, payments, credits and refused actions for this member appear here, newest first.',
  errorTitle: 'Couldn’t load the timeline',
  errorBody: 'Something went wrong while loading this member’s timeline.',
  loadMoreErrorBody: 'Couldn’t load more of the timeline.',
  retry: 'Try again',
  notRecorded: 'Not recorded',
  viewReservation: 'View reservation',
  viewPayment: 'View payment',
  /** Followed by the order's receipt number (DNKADM-0473). */
  viewOrder: 'View order',
  orderTitle: 'Order',
  orderLoading: 'Loading order',
  orderError: 'The order could not be loaded. Please try again.',
  orderLineFallback: 'Item',
  orderClose: 'Close',
  balance: 'Balance',
  credits: 'credits',
} as const;

/** The category chips, in the order the contract lists them. */
export const TIMELINE_CATEGORY_LABELS: Record<TimelineCategory, string> = {
  BOOKING: 'Bookings',
  MONEY: 'Money',
  CREDITS: 'Credits',
  REFUSED: 'Refused',
};

/** The actor-type fallback when the API names the type but neither a name nor an email. */
const ACTOR_TYPE_LABELS: Record<NonNullable<TimelineActor['type']>, string> = {
  ADMIN: 'Staff',
  MEMBER: 'Member app',
  GUEST: 'Guest checkout',
  SYSTEM: 'System',
};

/**
 * Who acted, unscrubbed (the caller scrubs). `null` actor is the honest "the API cannot
 * be sure" (DNKAPI-0620) and reads "Not recorded"; it is never guessed.
 */
export function timelineActorLabel(actor: TimelineActor | null): string {
  if (actor === null) return TIMELINE_COPY.notRecorded;
  const who = actor.name ?? actor.email;
  if (who !== null) return who;
  return actor.type !== null ? ACTOR_TYPE_LABELS[actor.type] : TIMELINE_COPY.notRecorded;
}

/**
 * The money direction from `kind` (`amountMinor` is unsigned). From the member's side: a
 * refund is money back (`+`), a charge is money paid (`-`). A decline, a pending payment
 * and a check-in charge choice moved no money yet, so they carry no sign; so does a kind
 * this build does not know.
 */
export function moneySign(kind: string | null): '+' | '-' | '' {
  if (kind === 'REFUND') return '+';
  if (kind === 'CHARGE') return '-';
  return '';
}

/**
 * A money row whose amount did NOT move: a declined or still-pending payment. Its amount
 * renders muted (a decline also struck through), never in the colour of a real charge.
 */
export function isUnmovedMoney(kind: string | null): boolean {
  return kind === 'DECLINE' || kind === 'PAYMENT_PENDING';
}

/** A signed credit delta as text, e.g. `+5` / `-1.5`. */
export function signedCredits(change: number, formatted: string): string {
  return change > 0 ? `+${formatted}` : change < 0 ? `-${formatted}` : formatted;
}

/** The icon family a KNOWN kind takes; `'refused'` gets the warning tint. */
export type TimelineIconFamily = ActivityKind | 'refused';

const KIND_FAMILY: Record<string, TimelineIconFamily> = {
  BOOKED: 'reservation',
  CONFIRMED: 'reservation',
  RESCHEDULED: 'reservation',
  CANCELLED: 'reservation',
  CHECKED_IN: 'reservation',
  NO_SHOW: 'reservation',
  ACTION_REFUSED: 'refused',
  COMP_REFUSED: 'refused',
  COMPS_LOCKED: 'refused',
  COMP: 'purchase',
  ORDER_VOID: 'purchase',
  CHARGE_WAIVED: 'purchase',
  CHARGE: 'purchase',
  DECLINE: 'purchase',
  REFUND: 'purchase',
  PAYMENT_PENDING: 'purchase',
  MEMBERSHIP_SET: 'membership',
  CREDIT_GRANT: 'credit',
  CREDIT_DEBIT: 'credit',
  CREDIT_REFUND: 'credit',
  CREDIT_EXPIRY: 'credit',
  CREDIT_ADJUST: 'credit',
};

/** The icon family for a kind, or `null` for a kind the API added after this build (no icon). */
export function timelineIconFamily(kind: string | null): TimelineIconFamily | null {
  if (kind === null) return null;
  return Object.prototype.hasOwnProperty.call(KIND_FAMILY, kind) ? (KIND_FAMILY[kind] ?? null) : null;
}

/** The row's heading: the server's title, else the raw kind, else a dash. Unscrubbed. */
export function timelineTitle(item: TimelineItem): string {
  return item.title ?? item.kind ?? '—';
}

/** The refused-glyph used for the `refused` family. */
export const REFUSED_ICON = 'block';

/** Why a cancel kept credit (DNKADM-0449). No window hours: the API does not send them. */
export const CANCEL_KEPT_REASON_TEXT: Record<CancelCreditReason, string> = {
  INSIDE_POLICY_WINDOW: 'cancelled inside the cancellation window',
  STAFF_NONE: 'staff chose nothing back',
  REFUND_LOCKED: 'rescheduled booking, no refund on cancel',
};

function creditsPhrase(n: number, bucketName: string | null): string {
  return formatCreditAmount(n, bucketName);
}

/**
 * The cancel outcome lines under "Reservation cancelled": a kept line (with its reason
 * clause when the reason is known) and/or a returned line. Empty when nothing was
 * recorded. Unscrubbed (the caller scrubs the bucket name).
 */
export function cancelOutcomeLines(outcome: CancelCreditOutcome | null): string[] {
  if (outcome === null) return [];
  const lines: string[] = [];
  const kept = outcome.creditsKept;
  if (kept !== null && kept > 0) {
    const reasonText = outcome.reason !== null ? CANCEL_KEPT_REASON_TEXT[outcome.reason] : undefined;
    const base = `${creditsPhrase(kept, outcome.bucketName)} kept`;
    lines.push(reasonText !== undefined ? `${base}: ${reasonText}` : base);
  }
  const returned = outcome.creditsReturned;
  if (returned !== null && returned > 0) {
    lines.push(`${creditsPhrase(returned, outcome.bucketName)} returned`);
  }
  return lines;
}

/**
 * The multi-lot line under a folded credit item (DNKAPI-0687): one booking debit or cancel
 * refund that spanned several lots is ONE item, and this says so. `null` for a single row
 * or an older API that sends no `lotCount`.
 */
export function lotCountLine(lotCount: number | null | undefined): string | null {
  if (lotCount === null || lotCount === undefined || !Number.isInteger(lotCount) || lotCount < 2) {
    return null;
  }
  return `Across ${lotCount} credit lots`;
}

/**
 * The real expiry of the lot behind a CREDIT_EXPIRY item (DNKAPI-0687 `lotExpiresAt`), as
 * the lot's last usable day on the calendar `zone` (`expiryLabel` owns the exclusive-
 * boundary convention): "Lot was good through Aug 31". `occurredAt` is when the row was
 * written, which can be later. `null` when absent or unparseable: never a guess.
 */
export function lotExpiryLine(lotExpiresAt: string | null | undefined, zone: string | null): string | null {
  if (lotExpiresAt === null || lotExpiresAt === undefined) return null;
  if (!Number.isFinite(Date.parse(lotExpiresAt))) return null;
  const label = expiryLabel(lotExpiresAt, zone);
  return `Lot was ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}
